import { Fragment, useEffect, useState } from "react";
import { collection, addDoc, onSnapshot, query, where, updateDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import Modal from "../secondary/Modal";
import ExerciseModalContent from "./ExerciseModalContent";
import { getAuth } from "firebase/auth";
import { TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faPlus, faGripVertical, faCheck, faTimes, faEye } from "@fortawesome/free-solid-svg-icons";
import { mostrarError, mostrarExito, mostrarInfo } from "../../utils/Alertas";
import "./css/Gym.css";
import { hideLoading, showLoading } from "../../../redux/actions/loadingActions";
import { useDispatch } from "react-redux";

interface Category {
    id: string;
    nombre: string;
    userId: string;
}

interface Exercise {
    id: string;
    nombre: string;
    categoryId: string;
    userId: string;
    sets: any[];
    nota: string;
    color?: "negro" | "azul" | "rojo" | "verde";
    order?: number;
    dias?: string[];
    orderDias?: Record<string, number>;
    hidden?: boolean;
}

const Gym: React.FC = () => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [exercises, setExercises] = useState<Exercise[]>([]);
    const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState("");
    const [isExerciseModalOpen, setIsExerciseModalOpen] = useState(false);
    const [newExerciseName, setNewExerciseName] = useState("");
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [isOrdering, setIsOrdering] = useState(false);
    const [orderingCategoryId, setOrderingCategoryId] = useState<string | null>(null);
    const [tempExercises, setTempExercises] = useState<Exercise[]>([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const dispatch = useDispatch();
    const auth = getAuth();
    const user = auth.currentUser;
    const userId = user?.uid;
    const [viewMode, setViewMode] = useState<"categorias" | "dias">("dias");
    const [orderingDia, setOrderingDia] = useState<string | null>(null);
    const [tempExercisesDia, setTempExercisesDia] = useState<Exercise[]>([]);
    const [hiddenModalCategoryId, setHiddenModalCategoryId] = useState<string | null>(null);
    const [hiddenModalDia, setHiddenModalDia] = useState<string | null>(null);

    const DIAS_SEMANA = ["lunes", "martes", "miércoles", "jueves", "viernes", "sábado", "domingo"];

    // 🔥 Cargar categorías
    useEffect(() => {
        if (!userId) return;

        const q = query(collection(db, "categories_gym"), where("userId", "==", userId));

        return onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Category[];

            setCategories(data);
        });
    }, [userId]);

    // 🔥 Cargar ejercicios
    useEffect(() => {
        if (!userId) return;

        const q = query(collection(db, "exercises"), where("userId", "==", userId));

        return onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Exercise[];

            setExercises(data);
        });
    }, [userId]);

    // =========================
    // ➕ CREAR
    // =========================

    const handleAddCategory = async () => {
        if (!newCategoryName.trim()) {
            mostrarInfo('Por favor, ingresa una nueva categoria.');
            return;
        }

        dispatch(showLoading());
        try {
            await addDoc(collection(db, "categories_gym"), {
                nombre: newCategoryName,
                userId
            });

            setNewCategoryName("");
            setIsFormOpen(false);
            mostrarExito('Categoría añadida correctamente.');
        } catch (error) {
            console.error("Error añadiendo categoría: ", error);
            mostrarError('Error añadiendo categoría.');
        } finally {
            dispatch(hideLoading());
        }
    };

    const handleSaveExercise = async () => {
        if (!newExerciseName.trim() || !selectedCategoryId) {
            mostrarInfo('Por favor, ingresa un nuevo ejercicio.');
            return;
        }

        dispatch(showLoading());
        try {
            await addDoc(collection(db, "exercises"), {
                nombre: newExerciseName,
                categoryId: selectedCategoryId,
                userId,
                sets: [],
                nota: "",
                color: "negro",
                order: Date.now() // 🔥 clave
            });

            setNewExerciseName("");
            setSelectedCategoryId(null);
            setIsExerciseModalOpen(false);

            mostrarExito('Ejercicio añadido correctamente.');
        } catch (error) {
            console.error("Error añadiendo ejercicio: ", error);
            mostrarError('Error añadiendo ejercicio.');
        } finally {
            dispatch(hideLoading());
        }
    };

    // =========================
    // 🔥 ORDENAR
    // =========================

    const startOrdering = (categoryId: string) => {
        const list = exercises
            .filter(ex => ex.categoryId === categoryId && !ex.hidden)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        setTempExercises(list);
        setOrderingCategoryId(categoryId);
        setIsOrdering(true);
    };

    const moveExercise = (index: number, direction: number) => {
        const newList = [...tempExercises];
        const targetIndex = index + direction;

        if (targetIndex < 0 || targetIndex >= newList.length) return;

        [newList[index], newList[targetIndex]] = [newList[targetIndex], newList[index]];
        setTempExercises(newList);
    };

    const saveOrder = async () => {
        dispatch(showLoading());

        try {
            for (let i = 0; i < tempExercises.length; i++) {
                await updateDoc(doc(db, "exercises", tempExercises[i].id), {
                    order: i
                });
            }

            mostrarExito("Orden guardado correctamente.");
        } catch (error) {
            console.error("Error guardando orden: ", error);
            mostrarError("Error guardando orden.");
        } finally {
            dispatch(hideLoading());
            setIsOrdering(false);
            setOrderingCategoryId(null);
        }
    };

    const cancelOrder = () => {
        setIsOrdering(false);
        setOrderingCategoryId(null);
        setTempExercises([]);
    };

    const startOrderingDia = (dia: string) => {
        const list = exercises
            .filter(ex => (ex.dias ?? []).includes(dia) && !ex.hidden)
            .sort((a, b) => (a.orderDias?.[dia] ?? 0) - (b.orderDias?.[dia] ?? 0));
        setTempExercisesDia(list);
        setOrderingDia(dia);
        setIsOrdering(true);
    };

    const moveExerciseDia = (index: number, direction: number) => {
        const newList = [...tempExercisesDia];
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= newList.length) return;
        [newList[index], newList[targetIndex]] = [newList[targetIndex], newList[index]];
        setTempExercisesDia(newList);
    };

    const saveOrderDia = async () => {
        if (!orderingDia) return;
        dispatch(showLoading());
        try {
            for (let i = 0; i < tempExercisesDia.length; i++) {
                await updateDoc(doc(db, "exercises", tempExercisesDia[i].id), {
                    orderDias: {
                        ...(tempExercisesDia[i].orderDias ?? {}),
                        [orderingDia]: i
                    }
                });
            }
            mostrarExito("Orden guardado correctamente.");
        } catch (error) {
            mostrarError("Error guardando orden.");
        } finally {
            dispatch(hideLoading());
            setIsOrdering(false);
            setOrderingDia(null);
            setTempExercisesDia([]);
        }
    };

    const cancelOrderDia = () => {
        setIsOrdering(false);
        setOrderingDia(null);
        setTempExercisesDia([]);
    };

    const unhideExercise = async (exerciseId: string, categoryId?: string, dia?: string) => {
        try {
            await updateDoc(doc(db, "exercises", exerciseId), { hidden: false });
            mostrarExito("Ejercicio restaurado.");

            // Cierra el modal si era el último oculto
            if (categoryId) {
                const remaining = exercises.filter(ex => ex.categoryId === categoryId && ex.hidden && ex.id !== exerciseId);
                if (remaining.length === 0) setHiddenModalCategoryId(null);
            }
            if (dia) {
                const remaining = exercises.filter(ex => (ex.dias ?? []).includes(dia) && ex.hidden && ex.id !== exerciseId);
                if (remaining.length === 0) setHiddenModalDia(null);
            }
        } catch {
            mostrarError("Error restaurando ejercicio.");
        }
    };

    const toggleEditMode = () => {
        if (isEditMode) {
            setIsOrdering(false);
            setOrderingCategoryId(null);
            setTempExercises([]);
        }

        setIsEditMode(!isEditMode);
    };

    // =========================
    // UI
    // =========================

    return (
        <div className="container-fluid">
            <div className="text-center">
                <h2 className="page-heading">Control de pesos</h2>
            </div>

            <div className="container-fluid content pb-3">
                {/* FORM CATEGORIA */}
                <div className="toggle-form" onClick={() => setIsFormOpen(!isFormOpen)}>
                    <FontAwesomeIcon icon={isFormOpen ? faChevronUp : faChevronDown} />
                </div>

                {isFormOpen && (
                    <Fragment>
                        <TextField
                            label="Nueva categoría"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            fullWidth
                        />

                        <div className="category-actions-row">
                            {/* BOTÓN EDITAR */}
                            <button
                                type="button"
                                className={`edit-btn ${isEditMode ? "active" : ""}`}
                                onClick={toggleEditMode}
                            >
                                {isEditMode ? <FontAwesomeIcon icon={faTimes} /> : <>✏️</>}
                            </button>

                            {/* BOTÓN CAMBIAR VISTA */}
                            <button
                                type="button"
                                className="view-mode-btn"
                                onClick={() => setViewMode(v => v === "categorias" ? "dias" : "categorias")}
                            >
                                {viewMode === "categorias" ? "📅 Días" : "💪 Grupos"}
                            </button>

                            {/* FORM AÑADIR */}
                            <form
                                onSubmit={(e) => { e.preventDefault(); handleAddCategory(); }}
                                className="add-category-form"
                            >
                                <button type="submit" className="main-button">Añadir</button>
                            </form>
                        </div>
                    </Fragment>
                )}

                {/* ================================
                    VISTA: GRUPOS MUSCULARES
                ================================ */}
                {viewMode === "categorias" && categories.map(cat => {
                    const list = isOrdering && orderingCategoryId === cat.id
                        ? tempExercises
                        : exercises
                            .filter(ex => ex.categoryId === cat.id && !ex.hidden)
                            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                    const hiddenList = exercises.filter(ex => ex.categoryId === cat.id && ex.hidden);

                    return (
                        <div key={cat.id} className="category mt-4">
                            <div className="category-header">
                                <h3>{cat.nombre}</h3>
                                <div className="category-actions">
                                    {isEditMode && isOrdering && orderingCategoryId === cat.id ? (
                                        <>
                                            <button className="btn-save" onClick={saveOrder}>
                                                <FontAwesomeIcon icon={faCheck} />
                                            </button>
                                            <button className="btn-cancel" onClick={cancelOrder}>
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {isEditMode && (
                                                <button onClick={() => startOrdering(cat.id)}>
                                                    <FontAwesomeIcon icon={faGripVertical} />
                                                </button>
                                            )}
                                            {hiddenList.length > 0 && (
                                                <button
                                                    className="hidden-count-btn"
                                                    onClick={() => setHiddenModalCategoryId(cat.id)}
                                                >
                                                    <FontAwesomeIcon className="ojo-gym" icon={faEye} />
                                                    <span className="ps-1">{hiddenList.length}</span>
                                                </button>
                                            )}
                                            <button className="add-exercise-btn" onClick={() => {
                                                setSelectedCategoryId(cat.id);
                                                setIsExerciseModalOpen(true);
                                            }}>
                                                <FontAwesomeIcon icon={faPlus} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {list.map((ex, index) => (
                                <div
                                    key={ex.id}
                                    className="exercise"
                                    onClick={() => {
                                        const ordenandoEsta = isOrdering && orderingCategoryId === cat.id;
                                        if (!ordenandoEsta) setSelectedExercise(ex);
                                    }}
                                >
                                    {isEditMode && isOrdering && orderingCategoryId === cat.id && (
                                        <div className="drag-controls">
                                            <button className="drag-top" onClick={() => moveExercise(index, -1)}>↑</button>
                                            <button onClick={() => moveExercise(index, 1)}>↓</button>
                                        </div>
                                    )}
                                    <div className="exercise-content">
                                        <strong style={{
                                            color: ex.color === "azul" ? "#004aad"
                                                : ex.color === "rojo" ? "#b40808"
                                                : ex.color === "verde" ? "#1a7a1a"
                                                : "var(--exercise-color-negro)"
                                        }}>
                                            {ex.nombre}
                                        </strong>
                                        <div className="sets">
                                            {ex.sets?.length > 0 ? (
                                                ex.sets.map((set, i) => (
                                                    <div className="ps-3 p-1" key={i}>
                                                        • {set.peso}kg x {set.reps} {(set.tipo === "dropset" || set.tipo === "descendente") && " 🔻"}
                                                    </div>
                                                ))
                                            ) : (
                                                <div>Sin series todavía.</div>
                                            )}
                                        </div>
                                        {ex.nota && <p className="nota mt-1 mb-1">📝 {ex.nota}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                })}

                {/* ================================
                    VISTA: DÍAS DE LA SEMANA
                ================================ */}
                {viewMode === "dias" && DIAS_SEMANA.map(dia => {
                    const list = isOrdering && orderingDia === dia
                        ? tempExercisesDia
                        : exercises
                            .filter(ex => (ex.dias ?? []).includes(dia) && !ex.hidden)
                            .sort((a, b) => (a.orderDias?.[dia] ?? 0) - (b.orderDias?.[dia] ?? 0));

                    const hiddenList = exercises.filter(ex => (ex.dias ?? []).includes(dia) && ex.hidden);

                    return (
                        <div key={dia} className="category mt-4">
                            <div className="category-header">
                                <h3>{dia.charAt(0).toUpperCase() + dia.slice(1)}</h3>
                                <div className="category-actions">
                                    {isEditMode && isOrdering && orderingDia === dia ? (
                                        <>
                                            <button className="btn-save" onClick={saveOrderDia}>
                                                <FontAwesomeIcon icon={faCheck} />
                                            </button>
                                            <button className="btn-cancel" onClick={cancelOrderDia}>
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {isEditMode && list.length > 0 && (
                                                <button onClick={() => startOrderingDia(dia)}>
                                                    <FontAwesomeIcon icon={faGripVertical} />
                                                </button>
                                            )}
                                            {hiddenList.length > 0 && (
                                                <button
                                                    className="hidden-count-btn"
                                                    onClick={() => setHiddenModalDia(dia)}
                                                >
                                                    <FontAwesomeIcon  icon={faEye} />
                                                    <span className="ps-1">{hiddenList.length}</span>
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {list.length === 0 && !isOrdering && (
                                <p className="no-exercises-msg">Sin ejercicios asignados.</p>
                            )}

                            {list.map((ex, index) => (
                                <div
                                    key={ex.id}
                                    className="exercise"
                                    onClick={() => {
                                        const ordenandoEsta = isOrdering && orderingDia === dia;
                                        if (!ordenandoEsta) setSelectedExercise(ex);
                                    }}
                                >
                                    {isEditMode && isOrdering && orderingDia === dia && (
                                        <div className="drag-controls">
                                            <button onClick={() => moveExerciseDia(index, -1)}>↑</button>
                                            <button onClick={() => moveExerciseDia(index, 1)}>↓</button>
                                        </div>
                                    )}
                                    <div className="exercise-content">
                                        <strong style={{
                                            color: ex.color === "azul" ? "#004aad"
                                                : ex.color === "rojo" ? "#b40808"
                                                : ex.color === "verde" ? "#1a7a1a"
                                                : "var(--exercise-color-negro)"
                                        }}>
                                            {ex.nombre}
                                        </strong>
                                        <div className="sets">
                                            {ex.sets?.length > 0 ? (
                                                ex.sets.map((set, i) => (
                                                    <div className="ps-3 p-1" key={i}>
                                                        • {set.peso}kg x {set.reps} {(set.tipo === "dropset" || set.tipo === "descendente") && " 🔻"}
                                                    </div>
                                                ))
                                            ) : (
                                                <div>Sin series todavía.</div>
                                            )}
                                        </div>
                                        {ex.nota && <p className="nota mt-1 mb-1">📝 {ex.nota}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                })}

                {/* MODAL EDITAR */}
                {selectedExercise && userId && (
                    <Modal onClose={() => setSelectedExercise(null)}>
                        <ExerciseModalContent
                            exercise={selectedExercise}
                            userId={userId}
                            onClose={() => setSelectedExercise(null)}
                        />
                    </Modal>
                )}

                {/* MODAL CREAR */}
                {isExerciseModalOpen && (
                    <Modal onClose={() => setIsExerciseModalOpen(false)}>
                        <h2>Añadir Ejercicio</h2>
                        <form onSubmit={(e) => { e.preventDefault(); handleSaveExercise(); }}>
                            <TextField
                                label="Nombre del ejercicio"
                                value={newExerciseName}
                                onChange={(e) => setNewExerciseName(e.target.value)}
                                fullWidth
                            />
                            <button className="main-button">Añadir</button>
                        </form>
                    </Modal>
                )}

                {/* MODAL EJERCICIOS OCULTOS — CATEGORÍA */}
                {hiddenModalCategoryId && (
                    <Modal onClose={() => setHiddenModalCategoryId(null)}>
                        <div className="resultado-content">
                            <div className="resultado-header">
                                <h2>Ejercicios ocultos</h2>
                            </div>
                            <div className="resultado-body">
                                {exercises
                                    .filter(ex => ex.categoryId === hiddenModalCategoryId && ex.hidden)
                                    .map(ex => (
                                        <div key={ex.id} className="hidden-exercise-row">
                                            <span className="col-10" style={{
                                                color: ex.color === "azul" ? "#004aad"
                                                    : ex.color === "rojo" ? "#b40808"
                                                    : ex.color === "verde" ? "#1a7a1a"
                                                    : "var(--exercise-color-negro)",
                                                fontWeight: 600
                                            }}>
                                                {ex.nombre}
                                            </span>
                                            <button
                                                className="unhide-btn col-2"
                                                onClick={() => unhideExercise(ex.id, hiddenModalCategoryId!, undefined)}
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                        </div>
                                    ))
                                }
                            </div>
                            <div className="col-12">
                                <button className="reset-button" onClick={() => setHiddenModalCategoryId(null)}>
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}

                {/* MODAL EJERCICIOS OCULTOS — DÍA */}
                {hiddenModalDia && (
                    <Modal onClose={() => setHiddenModalDia(null)}>
                        <div className="resultado-content">
                            <div className="resultado-header">
                                <h2>Ejercicios ocultos</h2>
                            </div>

                            <div className="resultado-body">
                                {exercises
                                    .filter(ex => (ex.dias ?? []).includes(hiddenModalDia) && ex.hidden)
                                    .map(ex => (
                                        <div key={ex.id} className="hidden-exercise-row">
                                            <span className="col-10" style={{
                                                color: ex.color === "azul" ? "#004aad"
                                                    : ex.color === "rojo" ? "#b40808"
                                                    : ex.color === "verde" ? "#1a7a1a"
                                                    : "var(--exercise-color-negro)",
                                                fontWeight: 600
                                            }}>
                                                {ex.nombre}
                                            </span>
                                            <button
                                                className="unhide-btn col-2"
                                                onClick={() => unhideExercise(ex.id, undefined, hiddenModalDia)}
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                        </div>
                                    ))
                                }
                            </div>
                            <div className="col-12">
                                <button className="reset-button" onClick={() => setHiddenModalDia(null)}>
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}
            </div>
        </div>
    );
};

export default Gym;
